import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import ClientSearch, { normalizeClientName } from "@/components/customer-orders/ClientSearch";
import { crmApi, extractCrmError } from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function NotifyClientStockDialog({
  open,
  onOpenChange,
  token,
  product,
  onCreated,
}) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedClient(null);
      setNotes("");
      setSaving(false);
    }
  }, [open]);

  const productLabel = product
    ? [product.name, product.code ? `код ${product.code}` : null].filter(Boolean).join(" · ")
    : "";

  const handleSubmit = async () => {
    if (!token) {
      toast.error("Войдите, чтобы оформить уведомление");
      return;
    }
    if (!product?.id) {
      toast.error("Товар не выбран");
      return;
    }
    if (!selectedClient?.id) {
      toast.error("Выберите клиента");
      return;
    }

    setSaving(true);
    try {
      const created = await crmApi.createStockSubscription(token, {
        client_id: selectedClient.id,
        product_id: product.id,
        notes: notes.trim() || undefined,
      });
      toast.success(
        `Клиент ${normalizeClientName(selectedClient)} получит WhatsApp, когда товар появится`
      );
      onCreated?.(created);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось оформить уведомление"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-amber-600" />
            Сообщить клиенту о наличии
          </DialogTitle>
          <DialogDescription>
            Когда товар появится на складе, клиент получит сообщение в WhatsApp, что можно заказать.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {productLabel || "Товар не выбран"}
            {product && Number(product.in_stock) > 0 && (
              <p className="mt-1 text-xs text-amber-700">
                Товар уже в наличии — уведомление уйдёт сразу после сохранения.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Клиент</Label>
            <ClientSearch
              token={token}
              selectedClient={selectedClient}
              onSelect={setSelectedClient}
              disabled={saving}
              variant="catalog"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="stock-notify-notes">Комментарий (необязательно)</Label>
            <Textarea
              id="stock-notify-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Например: спрашивал вчера в WhatsApp"
              rows={2}
              disabled={saving}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !selectedClient}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
            Подписать на уведомление
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
